import { z } from "zod"

// Base types for API responses
export type ProjectRecipient = {
  _id: string
  name: string
  email: string
}

export type CostCode = {
  _id: string
  code: string
  description: string
  category?: string
}

export type CostCodeBudget = {
  costCodeId: CostCode
  budgetedHours: number
  costBudget?: number
}

export type Personnel = {
  _id: string
  contactId: {
    _id: string
    name: string
    email: string
  }
  role: string
  status: string
}

export type Seat = {
  _id: string
  role: string
  status: string
  userId?: {
    _id: string
    name: string
    email: string
  } | null
  projectId?: {
    _id: string
    projectCode: string
    title: string
  } | null
}

export type SeatAssignment = {
  seatId: {
    _id: string
    role: string
    userId: {
      _id: string
      name: string
      email: string
    } | null
  }
  role: string
  assignedAt: string
  removed: boolean
  removedAt?: string
}

export type ProjectBudget = {
  hours: number
  labourRate: number
  currency: "USD" | "CAD"
  amount: number
}

export type ProjectQuantities = {
  structural: {
    tonnage: number
    pieces: number
  }
  miscMetals: {
    tonnage: number
    pieces: number
  }
  metalDeck: {
    pieces: number
    sqft: number
  }
  cltPanels: {
    pieces: number
    sqft: number
  }
  glulam: {
    volumeM3: number
    pieces: number
  }
}

// Project model from API
export type Project = {
  _id: string
  organizationId: string
  projectCode: string
  clientId: ProjectRecipient
  title: string
  location: string
  status: ProjectStatus
  bidDate?: string
  awardDate?: string
  fabricationStartDate?: string
  fabricationEndDate?: string
  erectionStartDate?: string
  erectionEndDate?: string
  completionDate?: string
  recipients: {
    timesheetExternal: ProjectRecipient[]
    timesheetInternal: ProjectRecipient[]
    payrollInternal: ProjectRecipient[]
  }
  costCodeBudgets: CostCodeBudget[]
  totalBudgetHours: number
  budget: ProjectBudget
  quantities: ProjectQuantities
  seatAssignments: SeatAssignment[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

// Project metadata from API
export type ProjectMeta = {
  clients: ProjectRecipient[]
  personnelByRole: Record<string, Personnel[]>
  freeSeats: Record<string, Seat[]>
  availableSeatsByRole?: Record<string, Seat[]>
  activeCostCodes: CostCode[]
  orgDefaults: {
    currency: "USD" | "CAD"
  }
}

// Project import types
export type ProjectImportRow = {
  mappedRow: Record<string, string>
  conflictType: string[]
  rowIndex: number
}

export type ProjectImportAnalysis = {
  sampleHeaders: string[]
  sampleRows: string[][]
}

export type ProjectImportPreview = {
  parsedRecords: ProjectImportRow[]
}

export type ProjectImportConfirmation = {
  newRows: ProjectImportRow[]
  upsertRows: ProjectImportRow[]
}

export type ProjectImportResult = {
  created: number
  updated: number
}

// Project status enum
export type ProjectStatus =
  | "Planning"
  | "Bidding"
  | "Awarded"
  | "Fabrication"
  | "Erection"
  | "Completed"
  | "OnHold"
  | "Cancelled"

// Crew assignment per foreman
export type ProjectCrew = {
  foremanId: string
  ironworkerIds: string[]
  version: number
}

// Zod schemas for validation
export const projectStatusSchema = z.enum([
  "Planning",
  "Bidding",
  "Awarded",
  "Fabrication",
  "Erection",
  "Completed",
  "OnHold",
  "Cancelled",
])

export const projectBudgetSchema = z.object({
  hours: z.number().nonnegative().optional(),
  labourRate: z.number().nonnegative().optional(),
  currency: z.enum(["USD", "CAD"]).default("USD"),
})

export const projectQuantitiesSchema = z.object({
  structural: z
    .object({
      tonnage: z.number().nonnegative().optional(),
      pieces: z.number().nonnegative().optional(),
    })
    .optional(),
  miscMetals: z
    .object({
      tonnage: z.number().nonnegative().optional(),
      pieces: z.number().nonnegative().optional(),
    })
    .optional(),
  metalDeck: z
    .object({
      pieces: z.number().nonnegative().optional(),
      sqft: z.number().nonnegative().optional(),
    })
    .optional(),
  cltPanels: z
    .object({
      pieces: z.number().nonnegative().optional(),
      sqft: z.number().nonnegative().optional(),
    })
    .optional(),
  glulam: z
    .object({
      volumeM3: z.number().nonnegative().optional(),
      pieces: z.number().nonnegative().optional(),
    })
    .optional(),
})

export const projectFormSchema = z.object({
  projectCode: z.string().min(1, "Project code is required"),
  title: z.string().min(1, "Title is required"),
  clientId: z.string().min(1, "Client is required"),
  location: z.string().optional(),
  status: projectStatusSchema.default("Planning"),
  bidDate: z.string().optional().nullable(),
  awardDate: z.string().optional().nullable(),
  fabricationStartDate: z.string().optional().nullable(),
  fabricationEndDate: z.string().optional().nullable(),
  erectionStartDate: z.string().optional().nullable(),
  erectionEndDate: z.string().optional().nullable(),
  completionDate: z.string().optional().nullable(),
  projectManagerPersonnelId: z.string().min(1, "Project Manager is required"),
  foremanPersonnelIds: z.array(z.string()).optional(),
  budget: projectBudgetSchema.optional(),
  quantities: projectQuantitiesSchema.optional(),
  costCodeBudgets: z
    .array(
      z.object({
        costCodeId: z.string(),
        budgetedHours: z.number().nonnegative(),
      }),
    )
    .optional(),
})

export type ProjectFormValues = z.infer<typeof projectFormSchema>
